import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppConfig } from "../Configs/AppConfig";
import {
  acquireMqttLiveSocket,
  releaseMqttLiveSocket,
  subscribeMqttLiveConnection,
} from "./mqttLiveSocket";
import { _get } from "../Services/baseApi";
import { deviceSensorTelemetryChartUrl, deviceSensorTelemetryUrl } from "../Services/endpoints";
import {
  resolveSensorTelemetryDisplay,
  sensorTelemetryDedupeKey,
} from "../Utils/sensorTelemetryFields";
import {
  countLiveOnlyRows,
  mergeLiveChartTelemetryRows,
  mergeLiveTelemetryRows,
} from "../Utils/telemetryLiveMerge";
import {
  buildChartFilterApiParams,
  buildDateFilterApiParams,
  chartPeriodToDateRange,
  isDateFilterValid,
  resolveDateFilterRange,
  rowMatchesSensorDateFilter,
} from "../Utils/lidarTelemetryDateFilter";
import { rowMatchesDeviceRoleFilter } from "../Utils/deviceRoleFilterUtils";

export const SENSOR_TELEMETRY_LIVE_EVENT = "sensor_telemetry:live";

/** Max points requested for chart history (backend cap is 500). */
export const SENSOR_CHART_FETCH_LIMIT = 200;

function sensorTimeAccessor(row) {
  return row?.receivedAt;
}

export function useDeviceSensorTelemetry({
  deviceDbId,
  masterMqttId,
  slaveMqttId,
  page,
  limit,
  search,
  sortColumn,
  sortDirection,
  dateFilter,
  deviceRoleFilter = "",
  chartEnabled = false,
}) {
  const [rows, setRows] = useState([]);
  const [chartHistoryRows, setChartHistoryRows] = useState([]);
  const [chartPeriod, setChartPeriod] = useState(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [connectionError, setConnectionError] = useState(null);
  const [liveRows, setLiveRows] = useState([]);

  const mqttIds = useMemo(() => {
    return [masterMqttId, slaveMqttId]
      .map((id) => id?.trim())
      .filter(Boolean);
  }, [masterMqttId, slaveMqttId]);

  const mqttIdSet = useMemo(() => new Set(mqttIds), [mqttIds]);
  const mqttIdSetRef = useRef(mqttIdSet);
  mqttIdSetRef.current = mqttIdSet;

  const dateRange = useMemo(
    () =>
      resolveDateFilterRange(
        dateFilter?.preset,
        dateFilter?.fromDate,
        dateFilter?.toDate,
        dateFilter?.fromTime,
        dateFilter?.toTime,
      ),
    [
      dateFilter?.preset,
      dateFilter?.fromDate,
      dateFilter?.toDate,
      dateFilter?.fromTime,
      dateFilter?.toTime,
    ],
  );

  const dateApiParams = useMemo(
    () => buildDateFilterApiParams(dateFilter),
    [dateFilter],
  );

  const chartApiParams = useMemo(
    () => buildChartFilterApiParams(dateFilter),
    [dateFilter],
  );

  const filterReady = isDateFilterValid(dateFilter);

  const matchesRowFilters = useCallback(
    (row) =>
      rowMatchesSensorDateFilter(row, dateRange) &&
      rowMatchesDeviceRoleFilter(row, deviceRoleFilter),
    [dateRange, deviceRoleFilter],
  );

  const canPrependLive =
    page === 1 && sortDirection === "DESC" && sortColumn === "receivedAt";

  const canPrependLiveChart = chartEnabled;

  const fetchHistory = useCallback(async ({ silent = false } = {}) => {
    if (!deviceDbId || !filterReady) return;

    if (!silent) setIsLoading(true);
    try {
      const response = await _get(deviceSensorTelemetryUrl(deviceDbId), {
        page,
        limit,
        search: search || undefined,
        sortColumn,
        sortDirection,
        ...(deviceRoleFilter ? { deviceRole: deviceRoleFilter } : {}),
        ...dateApiParams,
      });
      const result = response?.data?.result ?? {};
      const data = result.data ?? [];
      setRows(data);
      setTotal(result.total ?? 0);
      if (!silent) setLiveRows([]);
    } catch {
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [
    deviceDbId,
    page,
    limit,
    search,
    sortColumn,
    sortDirection,
    deviceRoleFilter,
    dateApiParams,
    filterReady,
  ]);

  const fetchChartHistory = useCallback(async ({ silent = false } = {}) => {
    if (!deviceDbId || !chartEnabled || !filterReady) return;

    if (!silent) setIsChartLoading(true);
    try {
      const response = await _get(deviceSensorTelemetryChartUrl(deviceDbId), {
        search: search || undefined,
        ...(deviceRoleFilter ? { deviceRole: deviceRoleFilter } : {}),
        ...chartApiParams,
      });
      const result = response?.data?.result ?? {};
      setChartHistoryRows(result.points ?? []);
      setChartPeriod(result.period ?? null);
      if (!silent) setLiveRows([]);
    } catch {
      if (!silent) {
        setChartHistoryRows([]);
        setChartPeriod(null);
      }
    } finally {
      if (!silent) setIsChartLoading(false);
    }
  }, [deviceDbId, chartEnabled, search, deviceRoleFilter, chartApiParams, filterReady]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchChartHistory();
  }, [fetchChartHistory]);

  useEffect(() => {
    const url = AppConfig.SOCKET_URL;
    if (!url || mqttIds.length === 0) {
      setConnectionStatus(mqttIds.length === 0 ? "disconnected" : "error");
      if (!url) setConnectionError("MQTT live socket URL is not configured");
      return undefined;
    }

    setConnectionStatus("connecting");
    setConnectionError(null);

    const socket = acquireMqttLiveSocket(url);
    if (!socket) return undefined;

    const unsubscribeConnection = subscribeMqttLiveConnection(
      (status, error) => {
        setConnectionStatus(status);
        setConnectionError(error);
      },
    );

    const onSensorLive = (payload) => {
      if (!payload?.deviceId || !mqttIdSetRef.current.has(payload.deviceId)) {
        return;
      }

      const normalized = {
        ...resolveSensorTelemetryDisplay(payload),
        id: payload.id,
        topic: payload.topic,
        receivedAt: payload.receivedAt,
        _isNew: true,
      };

      setLiveRows((prev) => {
        const key = sensorTelemetryDedupeKey(normalized);
        if (prev.some((row) => sensorTelemetryDedupeKey(row) === key)) {
          return prev;
        }
        return [normalized, ...prev];
      });
    };

    socket.on(SENSOR_TELEMETRY_LIVE_EVENT, onSensorLive);

    return () => {
      socket.off(SENSOR_TELEMETRY_LIVE_EVENT, onSensorLive);
      unsubscribeConnection();
      releaseMqttLiveSocket();
    };
  }, [mqttIds.join("|")]);

  useEffect(() => {
    setLiveRows([]);
    setChartHistoryRows([]);
    setChartPeriod(null);
  }, [
    deviceDbId,
    dateFilter?.preset,
    dateFilter?.fromDate,
    dateFilter?.toDate,
    dateFilter?.fromTime,
    dateFilter?.toTime,
    deviceRoleFilter,
  ]);

  useEffect(() => {
    if (!liveRows.some((row) => row._isNew)) return undefined;

    const timer = setTimeout(() => {
      setLiveRows((prev) => prev.map((row) => ({ ...row, _isNew: false })));
    }, 2500);

    return () => clearTimeout(timer);
  }, [liveRows]);

  const displayRows = useMemo(() => {
    const historyRows = dateRange
      ? rows.filter((row) => rowMatchesSensorDateFilter(row, dateRange))
      : rows;

    if (!canPrependLive) return historyRows;

    return mergeLiveTelemetryRows({
      historyRows,
      liveRows,
      limit,
      dedupeKey: sensorTelemetryDedupeKey,
      matchesDateFilter: matchesRowFilters,
      dateRange,
      timeAccessor: sensorTimeAccessor,
    });
  }, [rows, liveRows, canPrependLive, limit, matchesRowFilters, dateRange]);

  const displayTotal = useMemo(() => {
    if (!canPrependLive) return total;

    return (
      total +
      countLiveOnlyRows({
        historyRows: rows,
        liveRows,
        dedupeKey: sensorTelemetryDedupeKey,
        matchesDateFilter: matchesRowFilters,
        dateRange,
        timeAccessor: sensorTimeAccessor,
      })
    );
  }, [total, rows, liveRows, canPrependLive, matchesRowFilters, dateRange]);

  const displayChartRows = useMemo(() => {
    if (!chartEnabled) return [];

    if (!canPrependLiveChart) return chartHistoryRows;

    return mergeLiveChartTelemetryRows({
      historyRows: chartHistoryRows,
      liveRows,
      dedupeKey: sensorTelemetryDedupeKey,
      matchesDateFilter: matchesRowFilters,
      dateRange,
      timeAccessor: sensorTimeAccessor,
    });
  }, [
    chartHistoryRows,
    liveRows,
    canPrependLiveChart,
    chartEnabled,
    matchesRowFilters,
    dateRange,
  ]);

  const chartDateRange = useMemo(() => {
    const fromApi = chartPeriodToDateRange(chartPeriod);
    if (fromApi) {
      return { ...fromApi, periodLabel: dateRange?.periodLabel };
    }
    return dateRange;
  }, [chartPeriod, dateRange]);

  return {
    rows: displayRows,
    chartRows: displayChartRows,
    total: displayTotal,
    isLoading,
    isChartLoading,
    connectionStatus,
    connectionError,
    refetch: fetchHistory,
    refetchChart: fetchChartHistory,
    dateRange,
    chartDateRange,
    filterReady,
  };
}
